import React from "react";
import { Outlet } from "react-router-dom";
import { Box } from "@chakra-ui/react";
import useIdentity from "@/features/ui/hooks/use-identity";
import useGetMySelf from "@/features/auth/use-get-my-self";
import useMembership from "@/shared/hooks/useMembership";
import PathPickAccessDeniedPanel, {
  PathPickAccessDeniedReason,
} from "@/features/pathpick/components/PathPickAccessDeniedPanel";

interface PathPickAccessGuardProps {
  // false면 바이어 여부만 본다 — 미가입 바이어도 들어와야 하는 멤버십 페이지용.
  requiresMembership?: boolean;
}

/**
 * 패스픽 페이지의 접근 가드.
 *
 * 지금까지는 GNB에서 메뉴를 감추는 것이 전부라 URL을 직접 치면 그냥 열렸다.
 * 모달까지 링크로 공유되기 시작하면 권한 없는 사람이 그 링크를 밟게 되므로 라우트에서 막는다.
 *
 * 판정 기준은 GNB(app-navigation)와 같다 — BUYER이면서 멤버십 상태가 NONE이 아닐 것.
 * 바이어가 아니면(에이전시 등) 멤버십을 시작할 수도 없으므로 멤버십 안내 없이 바이어 계정 안내만 한다.
 * 체험·구독 만료는 통과시킨다(페이지 안에서 PathPickMembershipExpiredPanel이 안내한다).
 * 멤버십 페이지는 requiresMembership={false}로 바이어만 거르고, 시작 가이드는 가드 밖에 두어 언제나 열린다.
 */
export default function PathPickAccessGuard({
  requiresMembership = true,
}: PathPickAccessGuardProps) {
  const identity = useIdentity();
  const { data: mySelf, isLoading: isMySelfLoading } = useGetMySelf();
  const { membershipStatus } = useMembership();

  // 회사 정보가 오기 전에는 membershipStatus가 NONE으로 떨어지므로, 미확정 구간에는 아무것도 그리지 않는다.
  // (이 분기가 없으면 정상 사용자에게도 권한 안내가 한 번 깜빡인다)
  const isUndetermined = isMySelfLoading || !mySelf || !identity?.company;
  if (isUndetermined) {
    return null;
  }

  let deniedReason: PathPickAccessDeniedReason | null = null;
  if (identity.company!.type !== "BUYER") {
    deniedReason = "NOT_BUYER";
  } else if (requiresMembership && membershipStatus === "NONE") {
    deniedReason = "NO_MEMBERSHIP";
  }

  if (deniedReason) {
    // 주소는 그대로 두고 화면만 대체한다 — 어떤 링크를 받았는지 사용자가 확인할 수 있게.
    return (
      <Box sx={{ padding: "16px", height: "100%", overflow: "auto" }}>
        <PathPickAccessDeniedPanel reason={deniedReason} />
      </Box>
    );
  }

  return <Outlet />;
}
